import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, FormGroup } from 'reactstrap';

import { EmbeddedForm, formShape } from '@techchange/components';
import {
	DangerAlert,
	SuccessAlert,
	useLanguageContext,
	appPropTypes,
	API_BASE_URL,
} from '@techchange/components';
import { messages } from '../../../../i18n';

import FormDisplayEntryPagination from './components/FormDisplayEntryPagination';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.FORM_ENTRIES,
	withCredentials: true
});

const propTypes = {

	/**
	 * Flag indicating if the content title should be visible.
	 */
	showTitle: PropTypes.bool,

	/**
	 * The form to display.
	 */
	form: formShape.isRequired,

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack.isRequired,

};

const ContentDisplay = (props) => {

	const {
		showTitle,
		form,
		userId,
		filestack,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Keep track of all the entries for the current user for this form
	const [formEntries, setFormEntries] = useState(null);

	// Keep track of the current form entry to view/create/edit
	const [currentEntry, setCurrentEntry] = useState(null);

	const [isEditing, setIsEditing] = useState(false);

	const [showErrorMsg, setShowErrorMsg] = useState(false);
	const [showSuccessMsg, setShowSuccessMsg] = useState(false);

	const refErrorAlert = useRef();
	const refSuccessAlert = useRef();

	const filestackSettings = Object.assign({}, filestack);

	// Check if we need to override the filestack path in production
	if (filestackSettings.production) {

		const sessionId = form.scope_id;
		const organizationId = form.organization_id;
		const formId = form.id;

		// Build the filestack path for files related to forms and form entries
		filestackSettings.path =
			`/organizations/${organizationId}/sessions/${sessionId}/forms/${formId}/`;

	}

	/**
	 * Handles saving an entry, i.e. both creation and updates.
	 *
	 * @param {object} data  The form entry data.
	 */
	const handleSave = (data) => {

		let promise;

		// Check if updating an existing entry for the current user
		if (data.id) {

			promise = axiosClient.patch(`/${data.id}`, data);

		// Otherwise, create a new entry
		} else {

			promise = axiosClient.post('/', data);

		}

		// Reset the API success/error messages
		setShowErrorMsg(false);
		setShowSuccessMsg(false);

		promise.then((response) => {

			const updatedEntry = Object.assign({}, response.data);

			setFormEntries((prev) => {

				// Create a copy of all the previous form entries for the user for this form
				const updatedFormEntries = [...prev];

				const existingIndex = updatedFormEntries.findIndex(
					(entry) => entry.id === updatedEntry.id);

				// Check if the user updated an existing form entry
				if (existingIndex >= 0) {

					updatedFormEntries[existingIndex] = updatedEntry;

				// Otherwise, a new form entry was created
				} else {

					updatedFormEntries.push(updatedEntry);

				}

				return updatedFormEntries;

			});

			setCurrentEntry(updatedEntry);

			setIsEditing(false);

			setShowSuccessMsg(true);

		}).catch((error) => {

			console.error(error);

			setShowErrorMsg(true);

		});

	};

	/**
	 * Handles clicking to change answers to the current entry.
	 */
	const onChangeAnswers = () => {

		setShowErrorMsg(false);
		setShowSuccessMsg(false);

		setIsEditing(true);

	};

	/**
	 * Handles clicking to view a specific entry for the current user for this form.
	 *
	 * @param {number} entryIndex  The index of the entry to view.
	 */
	const onGoToEntry = (entryIndex) => {

		// Update the current entry
		setCurrentEntry(Object.assign({}, formEntries[entryIndex]));

		setIsEditing(false);

		setShowErrorMsg(false);
		setShowSuccessMsg(false);

	};

	/**
	 * Handles clicking to create a new entry for the current user for this form.
	 */
	const onGoToNewEntry = () => {

		// Reset the current entry
		setCurrentEntry(null);

		setIsEditing(true);

		setShowErrorMsg(false);
		setShowSuccessMsg(false);

	};

	/**
	 * Handles clicking to cancel the creation or update of an entry.
	 */
	const handleCancel = () => {

		// Check if the user cancelled creating a new entry
		if (!currentEntry) {

			// Reset the current entry to be the most recent entry for the user for this form
			setCurrentEntry(Object.assign({}, formEntries[formEntries.length - 1]));

		}

		setIsEditing(false);

	};

	// Fetch all the form entries for the current user for this form
	useEffect(() => {

		if (!formEntries) {

			axiosClient.get('/', {
				params: {
					form_id: form.id,
					user_id: userId,
					scope: form.scope,
					scope_id: form.scope_id,
					sort_by: 'created_at',
					sort_order: 'ASC',
				}
			}).then((response) => {

				const allEntries = (response && response.data) || [];

				const mostRecentEntry = allEntries[allEntries.length - 1];

				setCurrentEntry(mostRecentEntry);

				setIsEditing(!mostRecentEntry);

				setFormEntries(allEntries);

			});

		}

	}, [form, formEntries, userId]);

	// Auto-scroll to the success/error message (if needed)
	useEffect(() => {

		if (showErrorMsg && refErrorAlert?.current) {
			refErrorAlert.current.scrollIntoView({ behavior: 'smooth' });
		}

		if (showSuccessMsg && refSuccessAlert?.current) {
			refSuccessAlert.current.scrollIntoView({ behavior: 'smooth' });
		}

	}, [showErrorMsg, showSuccessMsg]);

	// Check if we are still loading the associated form entries
	if (!formEntries) {
		return <h1 className="p-3"><i className="fas fa-circle-notch fa-spin" /></h1>;
	}

	// Check if the learner can submit multiple form entries
	const canSubmitMultipleEntries =
		!isEditing &&
		Boolean(currentEntry) &&
		form.allow_multiple_submissions;

	// Check if the learner can edit a form entry
	const canEditEntry =
		!isEditing &&
		Boolean(currentEntry) &&
		form.allow_editing_submissions;

	// Check if the learner has already submitted multiple entries for the form
	const showMultipleEntriesPagination = Boolean(currentEntry) && formEntries.length > 1;

	const index =
		formEntries.findIndex((entry) => currentEntry && entry.id === currentEntry.id);

	// Get the translated content for the form
	const formContent = chooseContentFromLanguage(form.form_contents);

	return (
		<div className="tc-content-builder-form-entry">
			<FormGroup>
				{showTitle && formContent?.name &&
					<h5 className="tc-content-builder-form-title">
						{formContent.name}
					</h5>
				}
				{formContent?.description &&
					<span className="tc-content-builder-form-description">
						{formContent.description}
					</span>
				}
			</FormGroup>
			{showErrorMsg &&
				<div ref={refErrorAlert}>
					<DangerAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_MSG_TEXT_SUBMIT_ERROR} />
					</DangerAlert>
				</div>
			}
			{showSuccessMsg &&
				<div ref={refSuccessAlert}>
					<SuccessAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_MSG_TEXT_SUBMIT_SUCCESS} />
					</SuccessAlert>
				</div>
			}
			{showMultipleEntriesPagination &&
				<div className="tc-content-builder-form-entry-multiple-entries-pagination">
					<FormDisplayEntryPagination
						formEntries={formEntries}
						currentIndex={index}
						type={form.type}
						goToEntry={onGoToEntry} />
				</div>
			}
			{(currentEntry || canEditEntry || canSubmitMultipleEntries) &&
				<div className="tc-content-builder-form-entry-metadata">
					{currentEntry &&
						<span className="timestamp">
							<FormattedMessage
								{...messages.TC_COMPONENTS_GENERAL_TEXT_SUBMITTED_DATE}
								values={{
									date: moment(currentEntry.updated_at).format('MMMM D, YYYY h:mm A'),
								}} />
						</span>
					}
					<div className="tc-content-builder-form-entry-actions">
						{canEditEntry &&
							<Button
								color="link"
								onClick={onChangeAnswers}>
								<FormattedMessage
									{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_BUTTON_TEXT_CHANGE_FORM_ENTRY} />
							</Button>
						}
						{canEditEntry && canSubmitMultipleEntries &&
							<span>{'|'}</span>
						}
						{canSubmitMultipleEntries &&
							<Button
								color="link"
								onClick={onGoToNewEntry}>
								<FormattedMessage
									{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_BUTTON_TEXT_SUBMIT_ANOTHER_FORM_ENTRY} />
							</Button>
						}
					</div>
				</div>
			}
			<EmbeddedForm
				key={currentEntry?.id}
				form={form}
				formEntry={currentEntry}
				isEditing={isEditing}
				filestack={filestackSettings}
				onCancel={formEntries.length >= 1 ? handleCancel : null}
				onSave={handleSave} />
		</div>
	);

};

ContentDisplay.propTypes = propTypes;

export default ContentDisplay;
